import React from 'react';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import ProfileLayout from './ProfileLayout';
import ProfileLayoutMobile from './ProfileLayoutMobile';
import { useGetProfilePreferences } from '../../api/graphql_profile_preferences';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

/**
 * Picks desktop vs mobile chrome AND enforces the birthdate-required
 * gate. Any profile route — freshly-linked or pre-existing — that
 * lands here without a `birthdate` set on the profile preferences
 * gets bounced to `/profile/:pid/setup`. The setup screen is the
 * only profile-scoped surface that *isn't* gated, otherwise the
 * redirect would loop. Once the parent saves birthdate (and
 * gender), the gate stops firing and the original route renders.
 *
 * Why a layout-level gate (not a one-shot redirect after linking):
 * existing un-set profiles get the same prompt next time the
 * parent visits, instead of staying half-configured forever.
 */
export default function ProfileLayoutResponsive({ client }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { pid } = useParams<{ pid: string }>();
  const { pathname } = useLocation();

  const { data, loading } = useGetProfilePreferences(client, pid ?? null);

  // Don't gate the setup route itself, otherwise the redirect loops.
  const isOnSetup = !!pid && pathname.startsWith(`/profile/${pid}/setup`);

  if (pid && !loading && !isOnSetup) {
    const prefs = data?.getProfilePreferences;
    // `birthdate` is the load-bearing field — we use it for the
    // age-band rubric. Gender is collected at the same time but
    // birthdate alone gates.
    if (!prefs?.birthdate) {
      return <Navigate to={`/profile/${pid}/setup`} replace />;
    }
  }

  // Empty shell while the first prefs fetch is in flight, so the
  // parent doesn't see the dashboard flash before the gate kicks in.
  if (pid && loading && !data && !isOnSetup) {
    return <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }} />;
  }

  return isMobile
    ? <ProfileLayoutMobile client={client} />
    : <ProfileLayout client={client} />;
}
